import { motion } from 'framer-motion';
import { BookOpen, Feather } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';

interface TreslogEmptyStateProps {
  onCreateFirst: () => void;
}

export function TreslogEmptyState({ onCreateFirst }: TreslogEmptyStateProps) {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Tell your story from the outside looking in',
      description: 'Write about your life as if you were reading your own journey. TRESLOG helps you step back and see your story with clarity and compassion.',
      privacyNote: 'TRESLOG entries are private by default. This space is just for you.',
      cta: 'Write Your First Entry',
      nameExplanation: '"Tres" means three in Spanish, representing third-person perspective.',
    },
    es: {
      title: 'Cuenta tu historia desde afuera mirando hacia adentro',
      description: 'Escribe sobre tu vida como si estuvieras leyendo tu propio viaje. TRESLOG te ayuda a dar un paso atrás y ver tu historia con claridad y compasión.',
      privacyNote: 'Las entradas de TRESLOG son privadas por defecto. Este espacio es solo para ti.',
      cta: 'Escribe Tu Primera Entrada',
      nameExplanation: '"Tres" representa la perspectiva en tercera persona.',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center py-16 px-4 text-center"
    >
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-primary/10 rounded-full blur-2xl scale-150" />
        <div className="relative bg-gradient-to-br from-primary/20 to-primary/5 p-8 rounded-full">
          <BookOpen className="h-16 w-16 text-primary" />
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4 max-w-md">
        {t.title}
      </h2>

      <p className="text-muted-foreground mb-6 max-w-lg leading-relaxed">
        {t.description}
      </p>

      <div className="flex items-center gap-2 text-sm text-muted-foreground/80 mb-8 bg-muted/50 px-4 py-2 rounded-full">
        <Feather className="h-4 w-4" />
        <span>{t.privacyNote}</span>
      </div>

      <Button size="lg" onClick={onCreateFirst} className="gap-2">
        <BookOpen className="h-5 w-5" />
        {t.cta}
      </Button>

      <p className="text-xs text-muted-foreground/60 mt-6 italic">
        {t.nameExplanation}
      </p>
    </motion.div>
  );
}
